import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, SparklesIcon, TerminalSquareIcon, BracketsIcon } from 'lucide-react';
import { BuyButton } from './BuyButton';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const [count, setCount] = useState(from);
  useEffect(() => {
    let start = from;
    const duration = 1500;
    const increment = (to - from) / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= to) {
        setCount(to);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [from, to]);
  return <span data-id="element-199">{count}</span>;
}
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden" data-id="element-200">
      {/* Background Elements */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gold-radial opacity-50 pointer-events-none" data-id="element-201" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-2 gap-16 lg:gap-8 items-center relative z-10" data-id="element-202">
        {/* Left Content */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-2xl" data-id="element-203">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/5 mb-8" data-id="element-204">
            <SparklesIcon className="w-4 h-4 text-gold" data-id="element-205" />
            <span className="text-xs font-semibold text-gold tracking-widest uppercase" data-id="element-206">
              The Ultimate AI Library
            </span>
          </div>

          <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-6 text-balance uppercase" data-id="element-207">
            <AnimatedCounter from={0} to={500} data-id="element-208" /> AI Prompts <br data-id="element-209" />
            <span className="text-gold" data-id="element-210">for Professionals</span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-offwhite font-semibold mb-4 leading-tight" data-id="element-211">
            Stop Asking ChatGPT. Start Commanding It.
          </p>

          <p className="text-muted text-lg leading-relaxed mb-10 max-w-xl" data-id="element-212">
            Most people use AI wrong. They ask vague questions and get generic
            results. This system gives you 500 ready-to-use prompts designed to
            help professionals create content faster, save time, sell better and
            work smarter.
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 mb-12" data-id="element-213">
            <BuyButton className="w-full sm:w-auto px-8 py-4 bg-gold text-black font-bold rounded-lg hover:bg-gold-light transition-all shadow-gold-glow hover:shadow-gold-glow-lg transform hover:-translate-y-0.5" label="Get Instant Access" data-id="element-214" />
            <a href="#whats-inside" className="w-full sm:w-auto text-center px-8 py-4 border border-gold/30 text-offwhite font-bold rounded-lg hover:bg-white/5 hover:border-gold/50 transition-all" data-id="element-215">
              See What's Inside
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-8 border-t border-white/10 mb-8" data-id="element-216">
            {['Instant Access', '100% Digital', 'Ready-to-Use Prompts', 'Designed for Professionals'].map((text, i) => <div key={i} className="flex items-center gap-2" data-id="element-217">
                <CheckIcon className="w-4 h-4 text-gold shrink-0" data-id="element-218" />
                <span className="text-xs text-muted font-semibold" data-id="element-219">{text}</span>
              </div>)}
          </div>

          <div className="flex items-center gap-4 bg-white/5 p-3 rounded-xl border border-white/10 w-fit" data-id="element-220">
            <div className="flex -space-x-2" data-id="element-221">
              <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=200&h=200" className="w-9 h-9 rounded-full object-cover ring-2 ring-black" alt="User" data-id="element-222" />
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=200&h=200" className="w-9 h-9 rounded-full object-cover ring-2 ring-black" alt="User" data-id="element-223" />
              <img src="https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?auto=format&fit=crop&q=80&w=200&h=200" className="w-9 h-9 rounded-full object-cover ring-2 ring-black" alt="User" data-id="element-224" />
              <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=200&h=200" className="w-9 h-9 rounded-full object-cover ring-2 ring-black" alt="User" data-id="element-225" />
            </div>
            <div className="text-sm font-medium text-offwhite" data-id="element-226">
              Joined by <span className="text-gold font-bold" data-id="element-227">2,847+</span>{' '}
              professionals this month
            </div>
          </div>
        </motion.div>

        {/* Right Visual - Real Book Cover */}
        <div className="relative flex justify-center lg:justify-end items-center h-[420px] sm:h-[500px] lg:h-[600px]" data-id="element-228">
          {/* Floating Elements */}
          <motion.div animate={{
          y: [0, -15, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-10 right-4 text-gold/40 z-20" data-id="element-229">
            <TerminalSquareIcon className="w-8 h-8" data-id="element-230" />
          </motion.div>
          <motion.div animate={{
          y: [0, 20, 0]
        }} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute bottom-20 left-0 text-gold/30 z-20" data-id="element-231">
            <BracketsIcon className="w-10 h-10" data-id="element-232" />
          </motion.div>
          <motion.div animate={{
          y: [0, -10, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2
        }} className="absolute top-1/3 left-10 text-gold/40 z-20" data-id="element-233">
            <SparklesIcon className="w-6 h-6" data-id="element-234" />
          </motion.div>

          {/* Ambient gold glow */}
          <div className="absolute inset-0 bg-gold-radial opacity-60 blur-3xl scale-110 pointer-events-none" data-id="element-235" />

          <motion.img initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: 'easeOut'
        }} src="https://cdn.magicpatterns.com/uploads/9CCgJrxPoFhcx9F91uxb8e/ChatGPT_Image_May_26,_2026_at_01_59_00_PM.png" alt="500 AI Prompts for Professionals book cover" className="relative z-10 max-h-[400px] sm:max-h-[480px] lg:max-h-[600px] w-auto object-contain drop-shadow-[0_25px_60px_rgba(201,169,97,0.25)]" data-id="element-236" />
        </div>
      </div>
    </section>;
}